<template>
  <div class="col px-0">
    <v-overlay :value="loading">
      <v-progress-circular indeterminate size="64"></v-progress-circular>
    </v-overlay>

    <div class="text-subtitle-1 text-sm-h6 text-md-h5 font-weight-bold text-center">
      Relatório de Resultado de Produção
    </div>

    <v-card>
      <v-card-title>
        <span>Filtros</span>
      </v-card-title>
      <v-card-text>
        <v-row>
          <v-col cols="12" sm="7" md="5">
            <v-autocomplete
              v-model="harvestId"
              label="Safra"
              :items="harvestList"
              item-value="id"
              item-text="name"
              @change="loadCulturesFromHarvest"
              clearable
              @click:clear="harvestId = null; cultureList = []; cultureId = null"
              :loading="harvestListLoading"
            />
          </v-col>

          <v-col cols="12" sm="5" md="5">
            <v-autocomplete
              v-model="cultureId"
              label="Cultura (opcional)"
              :items="cultureList"
              item-value="id"
              item-text="name"
              :disabled="!harvestId"
              clearable
              @click:clear="cultureId = null"
              @change="loadPeriodsFromHarvestAndCulture"
              :loading="cultureListLoading"
              hint="Se nenhuma cultura for selecionada, todas serão exibidas"
              persistent-hint
            />
          </v-col>

          <v-col cols="12" sm="12" md="10">
            <v-autocomplete
              v-model="periodIds"
              label="Ciclos (opcional)"
              :items="periodList"
              item-value="id"
              item-text="name"
              :disabled="!harvestId"
              clearable
              @click:clear="periodIds = []"
              :loading="periodListLoading"
              multiple
              chips
              dense
              deletable-chips
              hint="Se nenhum talhão for selecionado, todos serão exibidos"
              persistent-hint
            >
              <template v-slot:selection="data">
                <v-chip
                  v-bind="data.attrs"
                  :input-value="data.selected"
                  close
                  @click="data.select"
                  @click:close="removeSelectedPeriod(data.item)"
                >
                  {{ data.item.name }} 
                  <small class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.additionalInfo}}) </small>
                  <small v-if="data.item.secondAdditionalInfo" class="font-italic font-weight-light ml-2 mt-1"> ({{data.item.secondAdditionalInfo}}) </small>
                </v-chip>
              </template>
              <template v-slot:item="data">
                <v-list-item-action>
                  <v-checkbox :input-value="data.attrs.inputValue"></v-checkbox>
                </v-list-item-action>
                <v-list-item-content>
                  <v-list-item-title class="text-subtitle-1">{{data.item.name}}</v-list-item-title>
                  <v-list-item-subtitle>
                    {{data.item.additionalInfo}}
                    <br v-if="data.item.secondAdditionalInfo">
                    {{data.item.secondAdditionalInfo}}
                  </v-list-item-subtitle>
                </v-list-item-content>
              </template>
            </v-autocomplete>
          </v-col>

          <v-col cols="12" sm="12" md="2">
            <v-btn 
              @click="loadProductionResultReport"
              :disabled="!harvestId"
              class="mt-2"
            >
              <v-icon left>mdi-magnify</v-icon>
              Filtrar
            </v-btn>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>

    <div>
      <BasePrint
        printableElementId="productionResultReportTable"
        printButtonLabel="Imprimir"
        printTitle="Relatório de Resultado de Produção"
        @start-printing="printing = loading = true"
        @stop-printing="printing = loading = false"
        v-if="productionResult"
        class="mt-3"
      />
    </div>

    <div id="productionResultReportTable">
      <v-simple-table 
        v-if="productionResult"
        class="mt-3"
      >
        <template v-slot:default>

          <thead>
            <tr>
              <th 
                class="text-left"
              >
                <span class="text-subtitle-1">Talhão</span>
              </th>
              <th 
                class="text-left"
                v-for="(period, index) in productionResult.periods"
                :key="period.name + period.id + index"
              >
                <span class="text-subtitle-1">{{period.fieldName}}</span>
              </th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Área</td>
              <td
                v-for="(period, index) in productionResult.periods"
                :key="period.area + '' + period.id + index"
              >
                {{ period.area | formatNumber }}
              </td>
            </tr>
            <tr>
              <td>Cultura</td>
              <td
                v-for="(period, index) in productionResult.periods"
                :key="period.cultureName + period.id + index"
              >
                {{ period.cultureName }}
              </td>
            </tr>
            <tr
              v-for="(item, index) in productionResult.items"
              :key="item.name + index"
              :class="item.type"
            >
              <td>{{item.name}}</td>
              <td
                v-for="period in productionResult.periods"
              >
                {{ numberToDecimal(getCostByPeriodId(item.costPeriods, period.id))}} <small class="text-caption" v-if="getCostHaByPeriodId(item.costPeriods, period.id) != 0">({{ numberToDecimal(getCostHaByPeriodId(item.costPeriods, period.id))}} por ha)</small>
              </td>
            </tr>
          </tbody>
        </template>
      </v-simple-table>

      <v-row class="mt-6" v-if="productionResult">
        <v-col cols="12" sm="6">
          <v-card>
            <v-card-title class="py-2">
              <span class="text-h6">Resultado da Operação</span>
            </v-card-title>

            <v-card-text class="text-right">
              <span :class="'font-weight-bold text-h6 ' + (productionResult.operationResult > 0 ? 'green--text' : 'red--text') ">
                {{numberToCurrency(productionResult.operationResult)}}
              </span>
            </v-card-text>

          </v-card>
        </v-col>

        <v-col cols="12" sm="6">
          <v-card>
            <v-card-title class="py-2">
              <span class="text-h6">Resultado Líquido %</span>
            </v-card-title>

            <v-card-text class="text-right">
              <span class="font-weight-bold text-h6">
                {{numberToPercentage(productionResult.netIncome)}}
              </span>
            </v-card-text>

          </v-card>
        </v-col>
      </v-row>
    </div>
    
  </div>
</template>

<script>
import gql from 'graphql-tag'
import moment from "moment";
import BaseIndex from "@shared_components/base_index.vue";
import DateRangeField from "@shared_components/date_range_field.vue"
import BasePrint from "@shared_components/base_print.vue";

export default {
  data() {
    return {
      tableItems: [],
      productionResult: null,
      loading: false,
      harvestListLoading: false,
      harvestList: [],
      harvestId: null,
      cultureList: [],
      cultureListLoading: false,
      cultureId: null,
      periodListLoading: false,
      periodList: [],
      periodIds: []
    };
  },
  props: {
  },
  components: {
    BaseIndex: BaseIndex,
    DateRangeField: DateRangeField,
    BasePrint: BasePrint,
  },
  created() {
    this.loadAllHarvests();
  },
  mounted() { 
  },
  computed: {
    tableHeaders: function () { }
  },
  methods: {
    loadProductionResultReport: function() {
      this.loading = true;
      this.$apollo
        .query({
          query: LOAD_PRODUCTION_RESULT_REPORT,
          variables: {
            harvestId: this.harvestId, 
            cultureId: this.cultureId, 
            periodIds: this.periodIds
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.productionResult = response.data.accountContext.productionResultReport;
          // console.log("productionResult", this.productionResult);
        })
        .catch(response => console.log(response))
        .finally(() => (this.loading = false));
    },
    getCostByPeriodId: function(periodsList, periodId) {
      let period = periodsList.find(period => period.periodId == periodId);
      if (period) {
        return period.cost;
      } 
      return 0;
    },
    getCostHaByPeriodId: function(periodsList, periodId) {
      let period = periodsList.find(period => period.periodId == periodId);
      if (period) {
        return period.costHa;
      } 
      return 0;
    },
    loadAllHarvests: function() {
        this.harvestListLoading = true;
        this.$apollo
          .query({
            query: LOAD_ALL_HARVEST_QUERY,
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.harvestList = response.data.accountContext.allHarvests;
          })
          .catch(response => console.log(response))
          .finally(() => (this.harvestListLoading = false));
    },
    loadCulturesFromHarvest: function() {
      if (this.harvestId){
        this.cultureListLoading = true;
        this.$apollo
          .query({
            query: CULTURES_FROM_HARVEST_QUERY,
            variables: {
              harvestId: this.harvestId
            },
            fetchPolicy: "no-cache"
          })
          .then(response => {
            this.cultureList = response.data.accountContext.allCulturesFromHarvest;
            this.cultureId = null;
            this.periodIds = [];
          })
          .catch(response => console.log(response))
          .finally(() => (this.cultureListLoading = false));
        
        this.loadPeriodsFromHarvestAndCulture();
      }
    },
    loadPeriodsFromHarvestAndCulture: function() {
      this.periodListLoading = true;
      this.$apollo
        .query({
          query: PERIODS_FROM_HARVEST_AND_CULTURES_QUERY,
          variables: {
            harvestId: this.harvestId,
            cultureId: this.cultureId
          },
          fetchPolicy: "no-cache"
        })
        .then(response => {
          this.periodList = response.data.accountContext.allPeriods;
          this.periodIds = [];
        })
        .catch(response => console.log(response))
        .finally(() => (this.periodListLoading = false));
    },
    removeSelectedPeriod: function(item) {
      const index = this.periodIds.indexOf(item.id)
      if (index >= 0) this.periodIds.splice(index, 1)
    },
  }
};



const LOAD_PRODUCTION_RESULT_REPORT = gql `
query ($harvestId: ID!, $cultureId: ID, $periodIds: [ID!]) {
  accountContext {
    productionResultReport(harvestId: $harvestId, cultureId: $cultureId, periodIds: $periodIds) {
      periods {
        id
        name
        area
        fieldName
        cultureName
      }
      items {
        name
        type
        costPeriods {
          periodId
          cost
          costHa
        }
      }
      operationResult
      netIncome
    }
  }
}
`;

const LOAD_ALL_HARVEST_QUERY = gql `
  {
    accountContext {
      allHarvests {
        id
        name
      }
    }
  }
`;

const CULTURES_FROM_HARVEST_QUERY = gql `
  query ($harvestId: ID!) {
    accountContext {
      allCulturesFromHarvest(harvestId: $harvestId){
        id
        name
      }
    }
  }
`;

const PERIODS_FROM_HARVEST_AND_CULTURES_QUERY = gql `
  query ($harvestId: ID, $cultureId: ID) {
    accountContext {
      allPeriods(harvestId: $harvestId, cultureId: $cultureId){
        id
        name
        additionalInfo
        secondAdditionalInfo
      }
    }
  }
`;

</script>

<style scoped>
  ::v-deep .cash_inflow_item {
    color: blue;
  }
  ::v-deep .cash_outflow_item {
    color: red;
  }
  ::v-deep .group_type {
    background-color: #ffdc9d;
    font-weight: bold;
  }
  ::v-deep .direct_cost {
    background-color: #60bd60;
    font-weight: bold;
  }
  ::v-deep .service_provision_cost {
    background-color: #60bd60;
    font-weight: bold;
  }
  ::v-deep .indirect_cost {
    background-color: #60bd60;
    font-weight: bold;
  }

  ::v-deep .total_cost {
    background-color: red;
    color: white;
    font-weight: bold;
  }
  ::v-deep .total_cost:hover {
    color: black;
  }
  
  ::v-deep .area_cost {
    background-color: darkgreen;
    color: white;
    font-weight: bold;
  }
  ::v-deep .area_cost:hover {
    color: black;
  }

  ::v-deep .unit_production_cost {
    background-color: darkgreen;
    color: white;
    font-weight: bold;
  }
  ::v-deep .unit_production_cost:hover {
    color: black;
  }
  
  ::v-deep .unit_cost {
    background-color: darkgreen;
    color: white;
    font-weight: bold;
  }
  ::v-deep .unit_cost:hover {
    color: black;
  }
  
  
  ::v-deep .total_revenue {
    background-color: #007eff;
    color: white;
    font-weight: bold;
  }
  ::v-deep .total_revenue:hover {
    color: black;
  }

  ::v-deep .period_result {
    background-color: yellow;
  }

  ::v-deep .area_result {
    background-color: yellow;
  }
  ::v-deep .unit_result {
    background-color: yellow;
  }
  ::v-deep .net_income {
    background-color: yellow;
  }
</style>
